// Provider i18n pour le dashboard web.
// - Initialise depuis localStorage ou la langue navigateur
// - Persiste localStorage à chaque changement
// - Expose useTranslation() qui retourne { t, lang, setLang }

import {
  detectBrowserLanguage,
  translate,
  type Language,
} from '@pharmaconnect/i18n'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'

const STORAGE_KEY = 'pharmaconnect.language'

interface I18nContextValue {
  lang: Language
  setLang: (l: Language) => void
  t: (key: string, vars?: Record<string, string | number>) => string
}

const I18nContext = createContext<I18nContextValue>({
  lang: 'fr',
  setLang: () => {},
  t: (k) => k,
})

const readInitialLang = (): Language => {
  if (typeof window === 'undefined') return 'fr'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === 'fr' || stored === 'en') return stored
  return detectBrowserLanguage()
}

interface ProviderProps {
  children: React.ReactNode
}

export function I18nProvider({ children }: ProviderProps) {
  const [lang, setLangState] = useState<Language>(readInitialLang)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, lang)
      // Met à jour l'attribut HTML pour l'accessibilité et le SEO
      document.documentElement.setAttribute('lang', lang)
    }
  }, [lang])

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => translate(lang, key, vars),
    [lang],
  )

  const setLang = useCallback((l: Language) => setLangState(l), [])

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>
  )
}

export const useTranslation = () => useContext(I18nContext)
